
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { phpApi, genericPhpApi } from "@/integrations/php/client";
import { useToast } from '@/components/ui/use-toast';
import { sendSMS } from '@/utils/smsUtils';
import { sendEmailNotification } from '@/utils/emailUtils';
import { fetchAdminLeads, fetchLeadMetrics } from '@/api/adminLeads';
import type { ServiceType } from '@/lib/serviceCatalog';
import type { LeadFormAnswer } from '@/utils/leadFormAnswers';

// Define and export the type definitions
export type { ServiceType } from '@/lib/serviceCatalog';
export type LeadStageType = "σεΕκκρεμότητα" | "έκλεισεΗΔουλειά" | "ήθελεΠληροφορίες" | "Νέο";

export interface Lead {
  id: string;
  name: string;
  phone: string;
  email?: string | null;
  address: string;
  postalCode: string;
  service: ServiceType;
  timeframe: string;
  comments?: string;
  formAnswers?: LeadFormAnswer[];
  stage: LeadStageType;
  partnerId: string | null;
  partnerName: string | null;
  createdAt: string;
  updatedAt: string;
  smsStatus: "sent" | "delivered" | "failed" | null;
  viewed: boolean;
  viewedAt: string | null;
  latitude: number | null;
  longitude: number | null;
  campaign?: string | null;
  source?: string | null;
  fbLeadId?: string | null;
}

interface LeadContextType {
  leads: Lead[];
  newLeadsCount: number;
  unassignedCount: number;
  lastRefreshedAt: Date | null;
  markLeadsAsSeen: () => void;
  addLead: (name: string, phone: string, address: string, postalCode: string, service: ServiceType, timeframe: string, comments?: string, latitude?: number | null, longitude?: number | null, attribution?: { source?: string; campaign?: string | null }) => Promise<Lead>;
  getLeadById: (id: string) => Lead | undefined;
  updateLeadStage: (id: string, stage: LeadStageType) => void;
  markLeadAsViewed: (id: string) => void;
  sendSMS: (leadId: string, message: string, phone: string, partnerId?: string, options?: { silent?: boolean }) => Promise<void>;
  sendEmail: (leadId: string, email: string, subject: string, message: string, partnerId?: string) => Promise<void>;
  refreshLeads: () => Promise<void>;
  assignLeadToPartner: (leadId: string, partnerId: string, notifyPartner?: boolean) => Promise<void>;
  deleteLead: (leadId: string) => Promise<void>;
}

const LeadContext = createContext<LeadContextType | undefined>(undefined);

export const LeadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [newLeadsCount, setNewLeadsCount] = useState(0);
  const [globalUnassignedCount, setGlobalUnassignedCount] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const knownLeadIds = useRef<Set<string>>(new Set());
  const initialLoadDone = useRef(false);
  const { toast } = useToast();

  const refreshLeads = useCallback(async () => {
    try {
      // The shared provider keeps only a small recent working set for headers,
      // detail navigation and legacy screens. Lists/reports use purpose-specific
      // paginated or aggregated APIs and never pull the full leads table.
      const [listResult, metrics] = await Promise.all([
        fetchAdminLeads({ page: 1, perPage: 100, sort: 'newest' }),
        fetchLeadMetrics('all'),
      ]);
      const formattedLeads: Lead[] = listResult.leads;
      setGlobalUnassignedCount(metrics.summary.unassigned);

      if (!initialLoadDone.current) {
        // First load — populate known IDs silently
        formattedLeads.forEach(l => knownLeadIds.current.add(l.id));
        initialLoadDone.current = true;
        setNewLeadsCount(0);
      } else {
        // Subsequent polls — detect brand-new leads
        const newOnes = formattedLeads.filter(l => !knownLeadIds.current.has(l.id));
        if (newOnes.length > 0) {
          newOnes.forEach(l => knownLeadIds.current.add(l.id));
          setNewLeadsCount(prev => prev + newOnes.length);
          toast({
            title: `🔔 ${newOnes.length === 1 ? 'Νέο Lead!' : `${newOnes.length} νέα Leads!`}`,
            description: newOnes.slice(0, 3).map(l => `${l.name} · ${l.service}`).join(' | '),
          });
          // Play notification sound
          try {
            const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
            if (AudioCtx) {
              const ctx = new AudioCtx();
              [0, 0.18].forEach((delay, i) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.connect(gain);
                gain.connect(ctx.destination);
                osc.type = 'sine';
                osc.frequency.setValueAtTime(i === 0 ? 880 : 1100, ctx.currentTime + delay);
                gain.gain.setValueAtTime(0.25, ctx.currentTime + delay);
                gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + delay + 0.35);
                osc.start(ctx.currentTime + delay);
                osc.stop(ctx.currentTime + delay + 0.35);
              });
            }
          } catch (_) {}
        }
      }

      setLeads(formattedLeads);
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error("Unexpected error fetching leads:", error);
    }
  }, [toast]);

  const markLeadsAsSeen = useCallback(() => {
    setLeads(current => {
      current.forEach(l => knownLeadIds.current.add(l.id));
      return current;
    });
    setNewLeadsCount(0);
  }, []);

  // Refresh a bounded recent window only. Purpose-specific list and report
  // screens use their own paginated/aggregated endpoints.
  useEffect(() => {
    refreshLeads();

    const REFRESH_MS = 5 * 60_000;
    let interval: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (interval === null) interval = setInterval(refreshLeads, REFRESH_MS);
    };
    const stop = () => {
      if (interval !== null) { clearInterval(interval); interval = null; }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshLeads();
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [refreshLeads]);

  const addLead = async (name: string, phone: string, address: string, postalCode: string, service: ServiceType, timeframe: string, comments?: string, latitude?: number | null, longitude?: number | null, attribution?: { source?: string; campaign?: string | null }) => {
    // Creation, geocoding, partner matching and notifications all happen in
    // api/submit-web-lead.php. That endpoint reuses the same findPartner() as the
    // Google Sheets intake, so a website lead is distributed exactly like a Sheets
    // lead. Doing it server-side also means the work completes even if the visitor
    // closes the tab straight after submitting — common on mobile ad traffic.
    const newLead: Lead = {
      id: '',
      name,
      phone,
      address,
      postalCode,
      service,
      timeframe,
      comments,
      stage: 'Νέο' as LeadStageType,
      partnerId: null,
      partnerName: null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      smsStatus: null,
      viewed: false,
      viewedAt: null,
      latitude: latitude || null,
      longitude: longitude || null,
    };

    try {
      const response = await fetch('/api/submit-web-lead.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
        },
        body: JSON.stringify({
          name,
          phone,
          address,
          postal_code: postalCode,
          service,
          timeframe,
          comments: comments ?? '',
          latitude: latitude ?? null,
          longitude: longitude ?? null,
          source: attribution?.source ?? 'web',
          campaign: attribution?.campaign ?? null,
        }),
      });

      const body = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error('Error adding lead:', body?.error || response.status);
        return null as unknown as Lead;
      }

      newLead.id = body.lead_id || '';
      newLead.partnerId = body.partner_id ?? null;

      setLeads(prevLeads => [...prevLeads, newLead]);
    } catch (error) {
      console.error('Unexpected error adding lead:', error);
      return null as unknown as Lead;
    }

    return newLead;
  };

  const getLeadById = (id: string): Lead | undefined => {
    return leads.find(lead => lead.id === id);
  };

  const updateLeadStage = async (id: string, stage: LeadStageType) => {
    try {
      const response = await fetch('/api/update-lead-stage.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
        },
        body: JSON.stringify({ lead_id: id, stage }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'Stage update failed');

      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === id ? { ...lead, stage, updatedAt: new Date().toISOString() } : lead
        )
      );
    } catch (error) {
      console.error("Unexpected error updating lead stage:", error);
    }
  };

  const markLeadAsViewed = async (id: string) => {
    try {
      // Determine source: if URL has referrer from email/sms tracking, note it
      const urlParams = new URLSearchParams(window.location.search);
      const source = urlParams.get('src') || urlParams.get('source') || 'direct';

      const response = await fetch('/api/mark-lead-viewed.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
        },
        body: JSON.stringify({
          lead_id: id,
          source,
        }),
      });

      if (!response.ok) {
        console.error("Error marking lead as viewed: HTTP", response.status);
        return;
      }

      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === id ? { ...lead, viewed: true, viewedAt: new Date().toISOString() } : lead
        )
      );
    } catch (error) {
      console.error("Unexpected error marking lead as viewed:", error);
    }
  };

  const sendSMS = async (leadId: string, message: string, phone: string, partnerId?: string, options?: { silent?: boolean }) => {
    try {
      const csrfToken = sessionStorage.getItem('csrf_token') || '';
      const response = await fetch('/api/send-sms.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          phone: phone,
          message: message,
          leadId: leadId,
          partnerId: partnerId,
        }),
      });

      if (!response.ok) {
        const responseText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}. ${responseText}`);
      }

      const data = await response.json();

      if (data.status === 'sent') {
        await phpApi
          .from('leads')
          .update({ sms_sent: true })
          .eq('id', leadId);
          
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? { ...lead, smsStatus: 'sent' as const, updatedAt: new Date().toISOString() } : lead
          )
        );

        // If we have a partner ID, log this in the data layer
        if (partnerId) {
          try {
            await genericPhpApi
              .from('sms_logs')
              .insert({
                lead_id: leadId,
                partner_id: partnerId,
                status: 'sent',
                message_id: data.messageId || null
              });
          } catch (logError) {
            console.error("Error logging SMS:", logError);
          }
        }

        if (!options?.silent) {
          toast({
            title: "SMS εστάλη επιτυχώς",
            description: "Το μήνυμα SMS εστάλη με επιτυχία.",
            variant: "default",
          });
        }
      } else {
        await phpApi
          .from('leads')
          .update({ sms_sent: false })
          .eq('id', leadId);
          
        setLeads(prevLeads =>
          prevLeads.map(lead =>
            lead.id === leadId ? { ...lead, smsStatus: 'failed' as const, updatedAt: new Date().toISOString() } : lead
          )
        );

        // Log failed SMS
        if (partnerId) {
          try {
            await genericPhpApi
              .from('sms_logs')
              .insert({
                lead_id: leadId,
                partner_id: partnerId,
                status: 'failed'
              });
          } catch (logError) {
            console.error("Error logging failed SMS:", logError);
          }
        }

        console.error("SMS sending failed:", data.error);
        if (!options?.silent) {
          toast({
            title: "Αποτυχία αποστολής SMS",
            description: data.error || "Υπήρξε πρόβλημα κατά την αποστολή του SMS.",
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error("Error sending SMS:", error);
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId ? { ...lead, smsStatus: 'failed' as const, updatedAt: new Date().toISOString() } : lead
        )
      );
      if (!options?.silent) {
        toast({
          title: "Αποτυχία αποστολής SMS",
          description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του SMS.",
          variant: "destructive",
        });
      }
    }
  };

  const sendEmail = async (leadId: string, email: string, subject: string, message: string, partnerId?: string) => {
    try {
      let lead = leads.find(lead => lead.id === leadId);
      
      if (!lead) {
        const { data: leadData } = await phpApi
          .from('leads')
          .select('*')
          .eq('id', leadId)
          .single();

        if (leadData) {
          lead = {
            id: leadData.id,
            name: leadData.name || '',
            phone: leadData.phone || '',
            address: leadData.location || leadData.address || '',
            postalCode: leadData.postal_code || '',
            service: leadData.service || '',
            timeframe: leadData.timeframe || '',
            comments: leadData.comments || '',
            stage: leadData.stage || 'Νέο',
            partnerId: leadData.partner_id || null,
            partnerName: null,
            createdAt: leadData.created_at ? leadData.created_at.replace(' ', 'T') + 'Z' : leadData.created_at,
            updatedAt: leadData.updated_at,
            smsStatus: leadData.sms_status || null,
            viewed: leadData.viewed || false,
            viewedAt: leadData.viewed_at || null,
            latitude: leadData.latitude || null,
            longitude: leadData.longitude || null,
          } as any;
        }
      }

      if (!lead) {
        throw new Error("Lead not found");
      }
      
      const leadDetails = {
        name: lead.name,
        service: lead.service,
        phone: lead.phone,
        address: lead.address,
        timeframe: lead.timeframe,
        comments: lead.comments
      };

      const result = await sendEmailNotification(email, leadId, leadDetails, partnerId);
      
      if (result.status === 'success') {
        toast({
          title: "Email εστάλη",
          description: "Το email εστάλη με επιτυχία.",
          variant: "default",
        });
      } else {
        toast({
          title: "Αποτυχία αποστολής email",
          description: result.error || "Υπήρξε πρόβλημα κατά την αποστολή του email.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error sending email:", error);
      toast({
        title: "Αποτυχία αποστολής email",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την αποστολή του email.",
        variant: "destructive",
      });
    }
  };

  const assignLeadToPartner = async (leadId: string, partnerId: string, notifyPartner: boolean = false) => {
    try {
      const response = await fetch('/api/admin/assign-lead.php', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': sessionStorage.getItem('csrf_token') || '',
        },
        body: JSON.stringify({ lead_id: leadId, partner_id: partnerId }),
      });
      const assignment = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(assignment?.error?.message || 'Assignment failed');

      // Get partner name and contact info
      const { data: partnerData, error: partnerError } = await phpApi
        .from('partners')
        .select('name, phone, email, active, sms_opt_out')
        .eq('id', partnerId)
        .single();

      if (partnerError) {
        console.error("Error fetching partner name:", partnerError);
        throw new Error(partnerError.message);
      }

      // Update local state
      setLeads(prevLeads =>
        prevLeads.map(lead =>
          lead.id === leadId
            ? {
                ...lead,
                partnerId,
                partnerName: partnerData?.name || null,
                updatedAt: new Date().toISOString()
              }
            : lead
        )
      );

      // Notify the partner if requested
      if (false && notifyPartner && partnerData) {
        // Look in current state first, fall back to fetching from DB
        let lead = leads.find(lead => lead.id === leadId);
        if (!lead) {
          // Lead was just added — state hasn't updated yet, fetch from DB
          const { data: leadData } = await phpApi
            .from('leads')
            .select('*')
            .eq('id', leadId)
            .single();
          if (leadData) {
            lead = {
              id: leadData.id,
              name: leadData.name || '',
              phone: leadData.phone || '',
              address: leadData.location || leadData.address || '',
              postalCode: leadData.postal_code || '',
              service: leadData.service || '',
              timeframe: leadData.timeframe || '',
              comments: leadData.comments || '',
              stage: leadData.stage || 'Νέο',
              partnerId: leadData.partner_id || null,
              partnerName: partnerData?.name || null,
              createdAt: leadData.created_at ? leadData.created_at.replace(' ', 'T') + 'Z' : leadData.created_at,
              updatedAt: leadData.updated_at,
              smsStatus: leadData.sms_status || null,
              viewed: leadData.viewed || false,
              viewedAt: leadData.viewed_at || null,
              latitude: leadData.latitude || null,
              longitude: leadData.longitude || null,
            } as any;
          }
        }
        if (lead && partnerData.active) {
          // Send SMS only if partner has phone and has NOT opted out
          if (partnerData.phone && !partnerData.sms_opt_out) {
            try {
              const smsMessage = `Έχετε νέο ενδιαφερόμενο! Δείτε: ${window.location.origin}/lead/${lead.id}`;
              await sendSMS(leadId, smsMessage, partnerData.phone, partnerId);
            } catch (partnerSmsError) {
              console.error(`Partner SMS failed for lead ${leadId}:`, partnerSmsError);
            }
          }

          // Customer SMS must be independent from partner opt-out state
          if (lead.phone) {
            try {
              const miniPageUrl = `${window.location.origin}/mini/${lead.id}`;
              const customerSmsMessage = `Ευχαριστούμε για το ενδιαφέρον σας! Ο συνεργάτης ${partnerData.name} θα επικοινωνήσει σύντομα μαζί σας. Στοιχεία συνεργάτη: ${miniPageUrl}`;
              await sendSMS(leadId, customerSmsMessage, lead.phone, undefined, { silent: true });
            } catch (customerSmsError) {
              console.error(`Customer SMS failed for lead ${leadId}:`, customerSmsError);
            }
          }
          
          // Send email only for active partner with email
          if (partnerData.email) {
            await sendEmail(leadId, partnerData.email, "Νέος Ενδιαφερόμενος", "", partnerId);
          }
        }
      }

      toast({
        title: "Επιτυχής ανάθεση",
        description: `Ο ενδιαφερόμενος ανατέθηκε στον συνεργάτη ${partnerData?.name}`,
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to assign lead to partner:", error);
      toast({
        title: "Αποτυχία ανάθεσης",
        description: error instanceof Error ? error.message : "Υπήρξε πρόβλημα κατά την ανάθεση του ενδιαφερόμενου.",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteLead = async (leadId: string): Promise<void> => {
    try {
      // First delete related SMS delivery status records
      // This will work with RLS because we're including the Admin-Id header via the genericPhpApi client
      const { error: deliveryStatusError } = await genericPhpApi
        .from('sms_delivery_status')
        .delete()
        .eq('lead_id', leadId);

      if (deliveryStatusError) {
        console.error("Error deleting related SMS delivery status:", deliveryStatusError);
        // Continue with deletion attempt even if this fails
      }

      // Delete related SMS logs records
      const { error: smsLogsError } = await genericPhpApi
        .from('sms_logs')
        .delete()
        .eq('lead_id', leadId);

      if (smsLogsError) {
        console.error("Error deleting related SMS logs:", smsLogsError);
        // Continue with deletion attempt even if this fails
      }

      // Finally delete the lead record
      const { error } = await phpApi
        .from('leads')
        .delete()
        .eq('id', leadId);

      if (error) {
        console.error("Error deleting lead:", error);
        throw new Error(error.message);
      }

      // Remove from local state
      setLeads(prevLeads => prevLeads.filter(lead => lead.id !== leadId));
    } catch (error) {
      console.error("Failed to delete lead:", error);
      throw error;
    }
  };

  const unassignedCount = globalUnassignedCount;

  const value: LeadContextType = {
    leads,
    newLeadsCount,
    unassignedCount,
    lastRefreshedAt,
    markLeadsAsSeen,
    addLead,
    getLeadById,
    updateLeadStage,
    markLeadAsViewed,
    sendSMS,
    sendEmail,
    refreshLeads,
    assignLeadToPartner,
    deleteLead,
  };

  return (
    <LeadContext.Provider value={value}>
      {children}
    </LeadContext.Provider>
  );
};

export const useLeads = (): LeadContextType => {
  const context = useContext(LeadContext);
  if (context === undefined) {
    throw new Error("useLeads must be used within a LeadProvider");
  }
  return context;
};
